import React, { useLayoutEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import mtgBanner from "@rosette/assets/src/ad/MTG-banner.png";
import collisionImage from "@rosette/assets/src/collision.png";
import { DISCORD_INVITE_URL, MASTERS_AD_URL } from "@rosette/ts/src/business/constants.js";
import { ArticleList } from "@/components/articles/ArticleList.js";
import { GameSetupPanel } from "@/components/gamesetup/GameSetupPanel.js";
import mastersUrBoards from "@rosette/assets/src/ad/masters-ur-boards.jpg";
import { latestBlogPost } from "@/app/blog/blogPosts.js";
import { useOptionalUser } from "@/components/env/EnvContext.js";
import type { GameClientControls } from "@rosette/ts/src/business/GameClientControls.js";
import { readCurrentDeviceInfo, PORTRAIT } from "@rosette/web_components/src/accessibility/DeviceInfoContext.js";
import { useRouter } from "next/navigation";
import { Panel } from "@rosette/web_components/src/ui/Panel.js";
import { HomeBackground } from "@rosette/web_components/src/layout/HomeBackground.js";
import Header from "@/components/layout/Header.js";
import type { GamePageParamValues } from "@/app/game/GamePageParamValues.js";


const COLUMN_CLASS_NAME = "block px-3 mx-auto max-w-3xl w-full";


interface GameSetupProps {
    clientControls: GameClientControls;
    paramValues: GamePageParamValues;
    setParamValues: (values: GamePageParamValues) => void;
    stripeTestMode: boolean;
}

/**
 * The user interface to choose the setup for playing a game.
 */
export default function GameSetup({
    clientControls, paramValues, setParamValues, stripeTestMode,
}: GameSetupProps) {
    const user = useOptionalUser();
    const router = useRouter();
    const h1Ref = useRef<HTMLHeadingElement | null>(null);

    useLayoutEffect(() => {
        // For some reason the anchor doesn't just work on its own.
        const orientation = readCurrentDeviceInfo().orientation;
        const anchor = window.location.hash;
        const h1 = h1Ref.current;
        if (orientation === PORTRAIT && anchor === "#play" && h1) {
            const targetY = h1.getBoundingClientRect().top + window.scrollY - 10;
            window.scroll({ top: targetY });
        }
    }, [router]);

    return (
        <div className="flex flex-col w-full">
            <HomeBackground>
                <Header />

                <div className={COLUMN_CLASS_NAME}>
                    <h1 id="play" ref={h1Ref} className="mt-6">
                        Play the Game of Ur
                    </h1>
                    <Panel>
                        <p>
                            The Royal Game of Ur is a 4500 year-old race game
                            {" "}
                            from ancient Mesopotamia.
                            {" "}
                            <Link href="/rules">
                                Learn the rules here.
                            </Link>
                        </p>
                    </Panel>
                </div>

                <div className="pt-6 pb-9">
                    <GameSetupPanel
                        className={COLUMN_CLASS_NAME}
                        clientControls={clientControls}
                        paramValues={paramValues}
                        setParamValues={setParamValues}
                        stripeTestMode={stripeTestMode} />
                </div>
            </HomeBackground>

            <div className={COLUMN_CLASS_NAME}>
                {!user && (
                    <section className="mb-6">
                        <h2>Latest Blog Post</h2>
                        <ArticleList articles={[latestBlogPost]} />
                    </section>
                )}

                <section className="mb-6">
                    <h2>Join Our Community!</h2>
                    <p>
                        Whether you&apos;re a casual player or history buff, a researcher, or a
                        pro player, everyone is welcome in our
                        {" "}
                        <Link href={DISCORD_INVITE_URL}>
                            Royal Game of Ur Discord group
                        </Link>
                        ! Many players find opponents in our group, discuss strategy,
                        and we even conduct research about the game. We also have channels
                        for discussing tournaments, DIY game boards, history, different rule
                        sets, similar games, and even the development of this website! However,
                        in my view, the best feature of our group is the community. I have made
                        many friends from our group, and if you&apos;re interested in the Royal Game
                        of Ur, I have no doubt you&apos;ll fit right in!
                    </p>
                    <div className="mb-9">
                        <Image
                            className="mx-auto w-auto max-h-[10em]"
                            src={collisionImage}
                            alt="A depiction of two game pieces colliding and producing sparks" />
                    </div>
                </section>

                <section className="mb-6">
                    <h2>Buy Physical Boards</h2>
                    <p>
                        We are obviously big fans of playing board games online here at
                        RoyalUr.net, but even we have to admit that there is something
                        special about playing on a real, physical board. If you are
                        looking to buy a physical board, we recommend that you check
                        out Masters Traditional Games! I have personally
                        met their founder, James Masters, and visited their warehouse in
                        St Albans. James Masters&apos; work as a games historian even led to
                        the Masters rule set here on RoyalUr.net! Purchases made by clicking
                        a link from RoyalUr.net will also help to support our
                        work.
                        {" "}
                        <Link rel="sponsored" href={MASTERS_AD_URL}>
                            Click here to visit Masters Traditional Games.
                        </Link>
                    </p>
                    <figure>
                        <Link rel="sponsored" href={MASTERS_AD_URL}>
                            <Image
                                src={mastersUrBoards}
                                alt="Photos of Royal Game of Ur boards sold by Masters Traditional Games"
                                className="drop-shadow-md rounded-sm" />
                        </Link>
                        <figcaption>
                            Photos of Royal Game of Ur boards sold by Masters Traditional Games.
                        </figcaption>
                    </figure>
                    <div className="mb-14">
                        <Link
                            className="drop-shadow-md"
                            rel="sponsored"
                            href={MASTERS_AD_URL}>

                            <Image src={mtgBanner} alt="Masters Traditional Games Banner Ad" />
                        </Link>
                    </div>
                </section>
            </div>
        </div>
    );
}
